import React, { useEffect, useState } from "react";
import {
  Card,
  Tabs,
  Button,
  Modal,
  Divider,
  Badge,
  Empty,
  Tag,
  Form,
  Input,
  DatePicker,
} from "antd";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowLeftOutlined,
  PlusOutlined,
  ProjectOutlined,
  UserOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import moment from "moment";
import axios from "axios";

import AddTask from "./AddTask";
import ByDeadline from "./ByDeadline";
import ViewHistory from "./ViewHistory";
import TaskCard from "./TaskCard";
import TaskService from "../../Services/Manage/TaskService";
import { ProjectService } from "../../Services/Manage/ProjectService";
import { UserService } from "../../Services/Manage/UserService";



const { TabPane } = Tabs;
const { TextArea } = Input;



const statusColors = {
  "To Do": "#1890ff",
  "In Progress": "#52c41a",
  Done: "#13c2c2",
  "On Hold": "#faad14",
  Archived: "#ff4d4f",
};

function TaskManagement() {
  const location = useLocation();
  const passedData = location.state || {};
  const projectId = passedData.projectId;

  const [addedBy, setAddedBy] = useState(null);


  const [engineers, setEngineers] = useState([]);

  const [rawTasks, setRawTasks] = useState([]);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [doneModalOpen, setDoneModalOpen] = useState(false);
  const [onHoldModalOpen, setOnHoldModalOpen] = useState(false);
  const [inProgressModalOpen, setInProgressModalOpen] = useState(false);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [viewHistoryOpen, setViewHistoryOpen] = useState(false);

  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedOnHoldTask, setSelectedOnHoldTask] = useState(null);
  const [selectedInProgressTask, setSelectedInProgressTask] = useState(null);
  const [selectedCommentTask, setSelectedCommentTask] = useState(null);
  const [selectedHistoryTask, setSelectedHistoryTask] = useState(null);

  const [form] = Form.useForm();
  const [doneForm] = Form.useForm();
  const [onHoldForm] = Form.useForm();
  const [inProgressForm] = Form.useForm();
  const [commentForm] = Form.useForm();

  const showAddModal = () => setOpenAddModal(true);
  const closeAddModal = () => setOpenAddModal(false);

  const parseEmployees = (str) => {
    try {
      const parsed = JSON.parse(str);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  };


  const fetchTasks = async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const resp = await TaskService.getTasksByProjectId(projectId);
      setRawTasks(Array.isArray(resp.data) ? resp.data : []);
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setRawTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const mapped = rawTasks.map((t) => {
      const employeeIds = parseEmployees(t.assigned_employee);
      const employeeNames = employeeIds
        .map((id) => engineers.find((e) => e.id === id)?.name)
        .filter(Boolean);

      return {
        key: t.id?.toString() || "",
        task: t.task_name || "",
        description: t.task_description || "",
        priority: t.priority || "",
        status: t.status || "",
        employees: employeeNames,
        dueDate: t.due_date || "",
        urgency: t.urgency || "",
        comment: t.comment || "",
        onHoldNote: t.on_hold_note || "",
        createdDate: t.added_on || moment().format("YYYY-MM-DD HH:mm:ss"),
        projectId: t.project_id,
      };
    });

    setTasks(mapped);
  }, [rawTasks, engineers]);



  const fetchProjectAddedBy = async () => {
    try {
      const response = await ProjectService.getAllProjectById(projectId);
      console.log("Project Data:", response.data);

      if (response.data && response.data.added_by) {
        setAddedBy(response.data.added_by);
      }
    } catch (error) {
      console.error("Error fetching project added_by:", error);
    }
  };

  const fetchProjectEngineers = async () => {
    try {
      const projectResp = await ProjectService.getAllProject();

      if (!projectResp || !projectResp.status || !projectResp.data) return;

      const project = projectResp.data.find((p) => p.id === projectId);
      if (!project || !project.pro_engineer) return;

      const engineerIds = JSON.parse(project.pro_engineer); // ["1709-0001", ...]

      const usersResp = await UserService.getAllUsers();
      const allUsers = usersResp.data;

      const engineerDetails = allUsers
        .filter((user) => engineerIds.includes(user.user_id))
        .map((user) => ({ id: user.user_id, name: user.name }));

      setEngineers(engineerDetails);
    } catch (err) {
      console.error("Error fetching project engineers:", err);
    }
  };





  useEffect(() => {
    if (projectId) {
      fetchTasks();
      fetchProjectAddedBy();
      fetchProjectEngineers();
    }
  }, [projectId]);


  const onFinishAdd = async (values) => {
    const payload = {
      added_by: addedBy,
      task_name: values.task,
      task_description: values.description,
      priority: values.priority,
      status: values.status,
      assigned_employee: JSON.stringify(values.employees),
      due_date: values.dueDate.format("YYYY-MM-DD"),
      urgency: values.urgency,
      project_id: projectId,
    };
    try {

      const response = await TaskService.createTask(payload);
      console.log(response)
      closeAddModal();
      form.resetFields();
      fetchTasks();
    } catch (err) {
      console.error("Error adding task:", err);
    }
  };


  const handleStatusChange = async (task, newStatus, extra = {}) => {
    if (!task || !task.key) return;

    const timelinePayload = {
      added_by: addedBy,
      task_id: parseInt(task.key, 10),
      status: newStatus,
      completion_date: extra.completion_date || null,
      note: extra.note || "",
      comment: extra.comment || "",
    };

    try {
      await TaskService.createTimeLine(timelinePayload);
      await TaskService.updateTask({ status: newStatus }, task.key);

      setTasks((prevTasks) =>
        prevTasks.map((t) =>
          t.key === task.key
            ? { ...t, status: newStatus, note: extra.note || "" }
            : t
        )
      );


      console.log("Timeline updated successfully");
    } catch (err) {
      console.error("Error updating task timeline:", err);
    }
  };



  const openDoneModal = (task) => {
    setSelectedTask(task);
    doneForm.resetFields();
    doneForm.setFieldsValue({
      completionDate: moment(),
      note: "",
    });
    setDoneModalOpen(true);
  };

  const openInProgressModal = (task) => {
    setSelectedInProgressTask(task);
    inProgressForm.resetFields();
    inProgressForm.setFieldsValue({
      note: "",
    });
    setInProgressModalOpen(true);
  };

  const openOnHoldModal = (task) => {
    setSelectedOnHoldTask(task);
    onHoldForm.resetFields();
    onHoldForm.setFieldsValue({
      reason: "",
      note: "",
    });
    setOnHoldModalOpen(true);
  };


  const openCommentModal = (task) => {
    setSelectedCommentTask(task);
    commentForm.resetFields();
    commentForm.setFieldsValue({
      comment: "",
    });
    setCommentModalOpen(true);
  };


  const openHistoryModal = (task) => {
    console.log(task);

    setSelectedHistoryTask(task);
    setViewHistoryOpen(true);
  };


  const handleDoneSubmit = async (values) => {
    if (!selectedTask) return;
    const extra = {
      completion_date: values.completionDate.format("YYYY-MM-DD"),
      note: values.note,
    };
    await handleStatusChange(selectedTask, "Done", extra);
    setDoneModalOpen(false);
  };

  const handleInProgressSubmit = async (values) => {
    if (!selectedInProgressTask) return;
    const extra = {
      note: values.note,
    };
    await handleStatusChange(selectedInProgressTask, "In Progress", extra);
    setInProgressModalOpen(false);
  };

  const handleOnHoldSubmit = async (values) => {
    if (!selectedOnHoldTask) return;

    const extra = {
      note: values.note,
      comment: values.reason, 
    };

    await handleStatusChange(selectedOnHoldTask, "On Hold", extra);
    setOnHoldModalOpen(false);
  };


  const handleCommentSubmit = async (values) => {
    if (!selectedCommentTask) return;
    try {
      const updatedTaskPayload = {
        task_name: selectedCommentTask.task,
        task_description: selectedCommentTask.description,
        priority: selectedCommentTask.priority,
        status: selectedCommentTask.status,
        assigned_employee: JSON.stringify(selectedCommentTask.employees),
        due_date: selectedCommentTask.dueDate,
        urgency: selectedCommentTask.urgency,
        project_id: selectedCommentTask.projectId,
        comment: values.comment,
      };

      const response = await TaskService.updateTask(updatedTaskPayload, selectedCommentTask.key);

      console.log(response);

      setTasks((prevTasks) =>
        prevTasks.map((t) =>
          t.key === selectedCommentTask.key ? { ...t, comment: values.comment } : t
        )
      );

      setCommentModalOpen(false);
    } catch (err) {
      console.error("Comment update error:", err);
    }
  };




  const closeHistoryModal = () => setViewHistoryOpen(false);

  const statuses = ["To Do", "In Progress", "Done", "On Hold"];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Card style={{ width: "100%", borderRadius: "10px" }} >
          <div style={{ marginBottom: "15px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}>
                <Link to="/manage-task">
                  <ArrowLeftOutlined style={{ fontSize: 20, color: "#0b0b0bff" }} />
                </Link>
                Create Task for Project
              </h2>
              <Button type="primary" onClick={showAddModal} size="large" icon={<PlusOutlined />}>
                Add Task
              </Button>
            </div>
          </div>

          {passedData && (
            <div style={{ marginTop: 24, marginBottom: 20, display: "flex", flexWrap: "wrap", gap: 16 }}>
              <Card style={{ flex: "1 1 240px", borderRadius: 10 }} bodyStyle={{ padding: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <ProjectOutlined style={{ fontSize: 28, color: "#1890ff" }} />
                  <div>
                    <div style={{ fontSize: 14, color: "#888" }}>Project Name</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: "#333" }}>
                      {passedData.projectName}
                    </div>
                  </div>
                </div>
              </Card>
              <Card style={{ flex: "1 1 240px", borderRadius: 10 }} bodyStyle={{ padding: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <UserOutlined style={{ fontSize: 28, color: "#1890ff" }} />
                  <div>
                    <div style={{ fontSize: 14, color: "#888" }}>Department</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: "#333" }}>
                      {passedData.department}
                    </div>
                  </div>
                </div>
              </Card>
              <Card style={{ flex: "1 1 240px", borderRadius: 10 }} bodyStyle={{ padding: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <CalendarOutlined style={{ fontSize: 28, color: "#1890ff" }} />
                  <div>
                    <div style={{ fontSize: 14, color: "#888" }}>Start Date</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: "#333" }}>
                      {passedData.startDate}
                    </div>
                  </div>
                </div>
              </Card>
              <Card style={{ flex: "1 1 240px", borderRadius: 10 }} bodyStyle={{ padding: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <CalendarOutlined style={{ fontSize: 28, color: "#faad14" }} />
                  <div>
                    <div style={{ fontSize: 14, color: "#888" }}>Target End Date</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: "#333" }}>
                      {passedData.endDate}
                    </div>
                  </div>
                </div>
              </Card>
              <Card style={{ flex: "1 1 240px", borderRadius: 10 }} bodyStyle={{ padding: 17 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Badge color="#1890ff" />
                  <div>
                    <div style={{ fontSize: 14, color: "#888" }}>Status</div>
                    <div>
                      <Tag color="blue" style={{ fontSize: 15 }}>
                        {passedData.status}
                      </Tag>
                    </div>
                  </div>
                </div>
              </Card>
              <Card style={{ flex: "1 1 240px", borderRadius: 10 }} bodyStyle={{ padding: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <CalendarOutlined style={{ fontSize: 28, color: "#13c2c2" }} />
                  <div>
                    <div style={{ fontSize: 14, color: "#888" }}>Added On</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: "#333" }}>
                      {passedData.addedOn}
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          <Tabs defaultActiveKey="1" tabBarStyle={{ fontSize: "16px", fontWeight: "600" }}>
            <TabPane tab={<span style={{ fontSize: 18, fontWeight: "600" }}>Task Tracker</span>} key="1">
              <div style={{ display: "flex", gap: "16px", justifyContent: "space-around", padding: "20px" }}>
                {statuses.slice(0, 4).map((status, idx) => (
                  <React.Fragment key={status}>
                    <div style={{ flex: 1 }}>
                      <Card
                        style={{
                          backgroundColor: "#f9f9f9",
                          border: "1px solid #e0e0e0",
                          borderRadius: 10,
                          marginBottom: 16,
                          minHeight: 240,
                          display: "flex",
                          flexDirection: "column",
                        }}
                        bodyStyle={{ padding: 12, flexGrow: 1, overflowY: "auto" }}
                      >
                        <h3 style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8, fontSize: 16, fontWeight: 600 }}>
                          {status}
                          <Badge
                            count={tasks.filter((t) => t.status === status).length}
                            overflowCount={999}
                            style={{ backgroundColor: statusColors[status] }}
                          />
                        </h3>
                        {tasks.filter((t) => t.status === status).length === 0 ? (
                          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No task" />
                          </div>
                        ) : (
                          tasks
                            .filter((t) => t.status === status)
                            .map((t) => (
                              <TaskCard
                                key={t.key}
                                task={t}
                                onMarkAsDone={openDoneModal}
                                onMarkInProgress={openInProgressModal}
                                onPutOnHold={openOnHoldModal}
                                onAddComment={openCommentModal}
                                onViewHistory={openHistoryModal}
                                onArchive={(task) => handleStatusChange(task, "Archived", { comment: "Archived" })}
                              />
                            ))
                        )}
                      </Card>
                    </div>
                    {idx < 3 && <Divider type="vertical" style={{ height: "auto", margin: "0 12px" }} />}
                  </React.Fragment>
                ))}
              </div>
            </TabPane>
            <TabPane tab={<span style={{ fontSize: 18, fontWeight: "600" }}>By Deadline</span>} key="2">
              <ByDeadline data={tasks} />
            </TabPane>
            <TabPane
              tab={
                <span style={{ fontSize: 18, fontWeight: "600" }}>
                  Archived Tasks{" "}
                  <Badge
                    count={tasks.filter((t) => t.status === "Archived").length}
                    overflowCount={999}
                    style={{ backgroundColor: statusColors["Archived"], marginLeft: 6, verticalAlign: "middle" }}
                  />
                </span>
              }
              key="3"
            >
              {tasks.filter((t) => t.status === "Archived").length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No archived tasks" />
              ) : (
                tasks
                  .filter((t) => t.status === "Archived")
                  .map((t) => (
                    <TaskCard
                      key={t.key}
                      task={t}
                      onMarkAsDone={openDoneModal}
                      onMarkInProgress={openInProgressModal}
                      onPutOnHold={openOnHoldModal}
                      onAddComment={openCommentModal}
                      onViewHistory={openHistoryModal}
                      onArchive={(task) => handleStatusChange(task, "Archived", { comment: "Archived" })}
                    />
                  ))
              )}
            </TabPane>
          </Tabs>
        </Card>
      </div>


      <AddTask open={openAddModal} onClose={closeAddModal} onFinish={onFinishAdd} form={form} engineers={engineers} />

      <Modal
        title={`Mark Task as Done: ${selectedTask?.task || ""}`}
        open={doneModalOpen}
        onCancel={() => setDoneModalOpen(false)}
        footer={null}
      >
        <Form form={doneForm} layout="vertical" onFinish={handleDoneSubmit} initialValues={{ completionDate: moment() }}>
          <Form.Item
            label="Completion Date"
            name="completionDate"
            rules={[{ required: true, message: "Please select a date!" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Note" name="note">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ width: "100%" }}>
              Mark as Done
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Put Task On Hold: ${selectedOnHoldTask?.task || ""}`}
        open={onHoldModalOpen}
        onCancel={() => setOnHoldModalOpen(false)}
        footer={null}
      >
        <Form form={onHoldForm} layout="vertical" onFinish={handleOnHoldSubmit}>
          <Form.Item label="Reason" name="reason" rules={[{ required: true, message: "Please enter reason!" }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Note" name="note">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ width: "100%" }}>Mark as On Hold</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Mark Task In Progress: ${selectedInProgressTask?.task || ""}`}
        open={inProgressModalOpen}
        onCancel={() => setInProgressModalOpen(false)}
        footer={null}
      >
        <Form form={inProgressForm} layout="vertical" onFinish={handleInProgressSubmit}>
          <Form.Item label="Note" name="note">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ width: "100%" }}>Mark as In Progress</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Add Comment: ${selectedCommentTask?.task || ""}`}
        open={commentModalOpen}
        onCancel={() => setCommentModalOpen(false)}
        footer={null}
      >
        <Form form={commentForm} layout="vertical" onFinish={handleCommentSubmit}>
          <Form.Item label="Comment" name="comment">
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ width: "100%" }}>Save Comment</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal open={viewHistoryOpen} onCancel={closeHistoryModal} footer={null} width={600}>
        {selectedHistoryTask && <ViewHistory task={selectedHistoryTask} />}
      </Modal>
    </div>
  );
}

export default TaskManagement;